import { DeployMessage, DeploySendAttempt } from '@core/models/deploy-plan-config.model';

/** `{}` deploys every message; a `uids` list deploys exactly those. */
export type DeployBody = Record<string, never> | { uids: string[] };

/** What the lock rules need of a message. */
type LockableMessage = Pick<DeployMessage, 'sends' | 'isValid'>;

/**
 * Whether a message already went out and the configuration forbids sending it again. The
 * backend refuses the whole request with `messages_already_deployed` and sends nothing —
 * not even the pending uids in it.
 *
 * A *successful* attempt, not any attempt: `assertNotAlreadySent` filters the requested
 * uids to those with one, so a message whose every attempt failed is still deployable and
 * locking it here would close a retry path the back end keeps open.
 *
 * Kept apart from `isSendLocked` because it is also the one reason a row is worth
 * labelling "already sent": the other lock has its own copy, and a message that was never
 * delivered must never wear that label.
 */
export function isResendLocked(
  message: Pick<DeployMessage, 'sends'>,
  allowRedeploy: boolean
): boolean {
  return !allowRedeploy && message.sends.some((attempt) => attempt.success);
}

/**
 * Whether a message cannot go into a deploy request at all, for either of the two reasons
 * a row is untickable: the channel could not build it, or it already went out and
 * re-sending is off.
 *
 * `is_valid` is channel-defined and is not a recipient count, so it is the only honest
 * answer to "would this one arrive": deploying it spends an attempt the channel has
 * already said will fail. The two reasons are independent — turning re-sending on unlocks
 * the second, never the first.
 */
export function isSendLocked(message: LockableMessage, allowRedeploy: boolean): boolean {
  return !message.isValid || isResendLocked(message, allowRedeploy);
}

/**
 * Whether any attempt on a message succeeded — GBE's own definition of sent, and the only safe
 * one to act on.
 *
 * `countOutcome` counts a route as sent when *any* row for it succeeded, and `upsertFailure`
 * deliberately never clears a successful row: the tour exists at GedMouv and `postTour` has no
 * cancel. So a later failure does not un-send anything, and anything that offers a message for
 * re-sending has to read this rather than the last attempt's outcome.
 */
export function hasSuccessfulSend(message: {
  sends: readonly DeploySendAttempt[];
}): boolean {
  return message.sends.some((attempt) => attempt.success);
}

/**
 * Attempted, and never got through. What the channel's failing-message list names, and what
 * `deriveChannelStats` counts as failed — the same predicate, so the list and the badge cannot
 * report different numbers.
 *
 * Not "the latest attempt failed": a message that went out and then failed on re-send is *sent*,
 * and naming it here would invite an operator to send a second copy of something already
 * delivered. On GEDMOUV that second copy is a permanent tour nobody can delete.
 */
export function failedWithoutSending(message: {
  sends: readonly DeploySendAttempt[];
}): boolean {
  return message.sends.length > 0 && !hasSuccessfulSend(message);
}

/**
 * Whether the *most recent* attempt on a message failed — which is what the row badges
 * `Failed`, and deliberately not what `deriveChannelStats` counts.
 *
 * The two answers differ for a message that went out and then failed on re-send: the counts call
 * it sent, because that is how GBE counts and the badge must not disagree with the back end,
 * while the row wears both `Sent` and `Failed`. Anything pointing the operator at rows to inspect
 * has to use this one, or it would omit a uid whose row is visibly red.
 */
export function lastAttemptFailed(message: {
  sends: readonly DeploySendAttempt[];
}): boolean {
  const last = message.sends[message.sends.length - 1];
  return last !== undefined && !last.success;
}

/**
 * The body a deploy request should carry, or `null` when there is nothing to deploy —
 * `{ uids: [] }` is refused and `{}` would send everything, so an empty selection has no
 * safe encoding and the caller must disable the action instead.
 *
 * Selected uids are intersected with the live list so a uid that disappeared between the
 * messages call and the deploy neither travels nor hides a full selection.
 */
export function buildDeployBody(
  selectedUids: ReadonlySet<string>,
  allUids: readonly string[],
  supportsDeployInSteps: boolean
): DeployBody | null {
  if (!supportsDeployInSteps) return {};
  if (selectedUids.size === 0) return null;

  const picked = allUids.filter((uid) => selectedUids.has(uid));
  if (picked.length === 0) return null;
  if (picked.length === allUids.length) return {};
  return { uids: picked };
}
